import { useEffect, useRef, useState } from "react";
import { useClientTranslations } from "../../lib/i18n/client";
import type { ContentFormat } from "../../services/educationService";
import styles from "./ViewAsDropdown.module.css";

interface ViewAsDropdownProps {
	availableFormats: ContentFormat[];
	onFormatSelect: (format: ContentFormat) => void;
	/**
	 * Which way the options panel opens. Defaults to "up" (used by ContentCard,
	 * where the trigger sits at the card's bottom). The fullscreen detail shell
	 * passes "down" because its trigger is at the top of the viewport — opening
	 * up would render the panel off-screen above it.
	 */
	openDirection?: "up" | "down";
}

const FORMAT_ICON_MAP: Record<ContentFormat, string> = {
	video: "icon-video.svg",
	audio: "icon-podcast.svg",
	text: "icon-article.svg",
	cards: "icon-cards.svg",
};

export function ViewAsDropdown({
	availableFormats,
	onFormatSelect,
	openDirection = "up",
}: ViewAsDropdownProps) {
	const { t } = useClientTranslations("education");
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	function handleOptionClick(format: ContentFormat) {
		onFormatSelect(format);
		setIsOpen(false);
	}

	return (
		<div className={styles.container} ref={containerRef}>
			{isOpen && (
				<div
					className={`${styles.panel} ${openDirection === "down" ? styles.panelDown : ""}`}
				>
					{availableFormats.map((format) => (
						<button
							key={format}
							type="button"
							className={styles.option}
							onClick={() => handleOptionClick(format)}
						>
							<img
								src={`/images/education/${FORMAT_ICON_MAP[format]}`}
								alt=""
								className={styles.optionIcon}
							/>
							<span className={styles.optionLabel}>
								{t(`formats.${format}`)}
							</span>
						</button>
					))}
				</div>
			)}
			<button
				type="button"
				className={`${styles.trigger} ${isOpen ? styles.triggerActive : ""}`}
				onClick={() => setIsOpen(!isOpen)}
			>
				<span className={styles.triggerLabel}>{t("actions.viewAs")}</span>
				<svg
					className={styles.triggerChevron}
					viewBox="0 0 12 12"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<title>Toggle</title>
					<path
						d="M3 4.5L6 7.5L9 4.5"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</button>
		</div>
	);
}
